import React, { useState, useMemo } from 'react';
import { 
  Trash2, 
  Plus, 
  Download, 
  Clipboard, 
  FileText, 
  Settings, 
  Play, 
  Check, 
  RefreshCw,
  Printer,
  Info,
  Users,
  Share2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Input } from '../ui';
import toast from 'react-hot-toast';
import { useNodeRoster } from '../../hooks/useNodeRoster';
import { NodeRosterPickerModal } from './common/NodeRosterPickerModal';
import { DirectoryMember } from '../../types';
import { DEFAULT_ENABLED_MODULES } from '../../constants';

// Core Type Definitions
interface SwimmerEntry {
  id: string;
  name: string;
  age: number;
  gender: 'M' | 'F' | 'Mixed';
  team: string;
  eventName: string;
  seedTime: string; // e.g. "28.50", "1:02.14" or "NT"
  seedSeconds: number; // calculated seconds for sorting, NT is Infinity
}

interface SwimEvent {
  id: string;
  number: number;
  name: string; // e.g. "Mixed 50 Yard Freestyle"
}

interface SeededSwimmer extends SwimmerEntry {
  heat: number;
  lane: number;
}

interface Heat {
  heatNumber: number;
  lanes: Record<number, SeededSwimmer>; // laneNumber -> Swimmer
}

interface SeededEvent {
  event: SwimEvent;
  heats: Heat[];
}

// Sample Data
const DEFAULT_EVENTS: SwimEvent[] = [
  { id: '1', number: 1, name: 'Mixed 50 Yard Freestyle' },
  { id: '2', number: 2, name: 'Girls 11-12 50 Yard Backstroke' },
  { id: '3', number: 3, name: 'Boys 13-14 100 Yard Butterfly' }
];

const DEFAULT_SWIMMERS: SwimmerEntry[] = [
  // Event 1: Mixed 50 Yard Freestyle
  { id: 's1', name: 'John Smith', age: 12, gender: 'M', team: 'WEST', eventName: 'Mixed 50 Yard Freestyle', seedTime: '35.40', seedSeconds: 35.4 },
  { id: 's2', name: 'Abby Jones', age: 11, gender: 'F', team: 'EAST', eventName: 'Mixed 50 Yard Freestyle', seedTime: '31.20', seedSeconds: 31.2 },
  { id: 's3', name: 'Zach Miller', age: 12, gender: 'M', team: 'WEST', eventName: 'Mixed 50 Yard Freestyle', seedTime: '33.10', seedSeconds: 33.1 },
  { id: 's4', name: 'Sarah Davis', age: 12, gender: 'F', team: 'EAST', eventName: 'Mixed 50 Yard Freestyle', seedTime: '36.20', seedSeconds: 36.2 },
  { id: 's5', name: 'Anna Wilson', age: 12, gender: 'F', team: 'WEST', eventName: 'Mixed 50 Yard Freestyle', seedTime: '30.10', seedSeconds: 30.1 },
  { id: 's6', name: 'Chris Taylor', age: 12, gender: 'M', team: 'EAST', eventName: 'Mixed 50 Yard Freestyle', seedTime: '28.50', seedSeconds: 28.5 },
  { id: 's7', name: 'Ryan Thomas', age: 12, gender: 'M', team: 'WEST', eventName: 'Mixed 50 Yard Freestyle', seedTime: '26.40', seedSeconds: 26.4 },
  { id: 's8', name: 'Emma White', age: 11, gender: 'F', team: 'EAST', eventName: 'Mixed 50 Yard Freestyle', seedTime: '27.80', seedSeconds: 27.8 },
  { id: 's9', name: 'Kyle Brown', age: 12, gender: 'M', team: 'WEST', eventName: 'Mixed 50 Yard Freestyle', seedTime: '29.90', seedSeconds: 29.9 },
  { id: 's10', name: 'Lily Green', age: 12, gender: 'F', team: 'EAST', eventName: 'Mixed 50 Yard Freestyle', seedTime: '30.50', seedSeconds: 30.5 },
  { id: 's11', name: 'Dave Harris', age: 11, gender: 'M', team: 'WEST', eventName: 'Mixed 50 Yard Freestyle', seedTime: 'NT', seedSeconds: Infinity },

  // Event 2: Girls 11-12 50 Yard Backstroke
  { id: 's12', name: 'Maya Patel', age: 11, gender: 'F', team: 'NORTH', eventName: 'Girls 11-12 50 Yard Backstroke', seedTime: '38.20', seedSeconds: 38.2 },
  { id: 's13', name: 'Zoe Chen', age: 12, gender: 'F', team: 'SOUTH', eventName: 'Girls 11-12 50 Yard Backstroke', seedTime: '34.10', seedSeconds: 34.1 },
  { id: 's14', name: 'Chloe L', age: 12, gender: 'F', team: 'NORTH', eventName: 'Girls 11-12 50 Yard Backstroke', seedTime: '42.15', seedSeconds: 42.15 },
  { id: 's15', name: 'Nora K', age: 11, gender: 'F', team: 'SOUTH', eventName: 'Girls 11-12 50 Yard Backstroke', seedTime: 'NT', seedSeconds: Infinity },
  { id: 's16', name: 'Sophia R', age: 11, gender: 'F', team: 'NORTH', eventName: 'Girls 11-12 50 Yard Backstroke', seedTime: '39.50', seedSeconds: 39.5 },

  // Event 3: Boys 13-14 100 Yard Butterfly
  { id: 's17', name: 'Luke W', age: 13, gender: 'M', team: 'WEST', eventName: 'Boys 13-14 100 Yard Butterfly', seedTime: '1:04.20', seedSeconds: 64.2 },
  { id: 's18', name: 'Liam O', age: 14, gender: 'M', team: 'EAST', eventName: 'Boys 13-14 100 Yard Butterfly', seedTime: '1:01.10', seedSeconds: 61.1 },
  { id: 's19', name: 'Owen M', age: 13, gender: 'M', team: 'WEST', eventName: 'Boys 13-14 100 Yard Butterfly', seedTime: '1:12.30', seedSeconds: 72.3 },
  { id: 's20', name: 'Jake S', age: 14, gender: 'M', team: 'EAST', eventName: 'Boys 13-14 100 Yard Butterfly', seedTime: '58.90', seedSeconds: 58.9 },
  { id: 's21', name: 'Connor F', age: 13, gender: 'M', team: 'EAST', eventName: 'Boys 13-14 100 Yard Butterfly', seedTime: '1:15.00', seedSeconds: 75.0 }
];

// Helper Functions for Time Parsing
const parseTimeToSeconds = (timeStr: string): number => {
  const clean = timeStr.trim().toUpperCase();
  if (!clean || clean === 'NT' || clean === 'NO TIME') {
    return Infinity;
  }
  const parts = clean.split(':');
  if (parts.length === 2) {
    const mins = parseFloat(parts[0]) || 0;
    const secs = parseFloat(parts[1]) || 0;
    return mins * 60 + secs;
  }
  return parseFloat(clean) || Infinity;
};

const formatSecondsToTime = (seconds: number): string => {
  if (seconds === Infinity || isNaN(seconds)) return 'NT';
  if (seconds < 60) {
    return seconds.toFixed(2);
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const secsStr = secs.toFixed(2);
  const paddedSecs = secsStr.indexOf('.') === 1 || secs < 10 ? `0${secsStr}` : secsStr;
  return `${mins}:${paddedSecs}`;
};

// Spearhead Seeding Lane Orders
const getLaneOrder = (numLanes: number): number[] => {
  switch (numLanes) {
    case 4: return [2, 3, 1, 4];
    case 6: return [3, 4, 2, 5, 1, 6];
    case 8: return [4, 5, 3, 6, 2, 7, 1, 8];
    case 10: return [5, 6, 4, 7, 3, 8, 2, 9, 1, 10];
    default: {
      const order: number[] = [];
      const center = Math.ceil(numLanes / 2);
      order.push(center);
      let step = 1;
      while (order.length < numLanes) {
        const nextRight = center + step;
        if (nextRight <= numLanes) order.push(nextRight);
        const nextLeft = center - step;
        if (nextLeft >= 1) order.push(nextLeft);
        step++;
      }
      return order;
    }
  }
};

export function SwimHeatSheetGenerator() {
  // Meet Settings
  const [meetName, setMeetName] = useState('Summer Invitational Meet');
  const [numLanes, setNumLanes] = useState(6);
  const [poolLength, setPoolLength] = useState('25');
  const [distanceUnit, setDistanceUnit] = useState<'Yards' | 'Meters'>('Yards');
  
  // Events & Swimmers States
  const [events, setEvents] = useState<SwimEvent[]>(DEFAULT_EVENTS);
  const [swimmers, setSwimmers] = useState<SwimmerEntry[]>(DEFAULT_SWIMMERS);

  // Organization Roster Integration
  const navigate = useNavigate();
  const { hasActiveNode, nodeName, members } = useNodeRoster();
  const [isRosterModalOpen, setIsRosterModalOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Bulk Import
  const [bulkInput, setBulkInput] = useState('');
  
  // Single Entry Form States
  const [newSwimmerName, setNewSwimmerName] = useState('');
  const [newSwimmerAge, setNewSwimmerAge] = useState('12');
  const [newSwimmerGender, setNewSwimmerGender] = useState<'M' | 'F' | 'Mixed'>('Mixed');
  const [newSwimmerTeam, setNewSwimmerTeam] = useState('WEST');
  const [newSwimmerEvent, setNewSwimmerEvent] = useState(DEFAULT_EVENTS[0]?.name || '');
  const [newSwimmerTime, setNewSwimmerTime] = useState('NT');

  // Single Event Form States
  const [newEventName, setNewEventName] = useState('');

  // UI Navigation
  const [activeTab, setActiveTab] = useState<'entries' | 'preview' | 'timers'>('entries');
  const [timerLaneFilter, setTimerLaneFilter] = useState<number | 'all'>('all');
  const [copySuccess, setCopySuccess] = useState(false);

  // Load Sample Data Helper
  const loadSampleData = () => {
    setEvents(DEFAULT_EVENTS);
    setSwimmers(DEFAULT_SWIMMERS);
  };

  // Clear All Data
  const clearAllData = () => {
    setEvents([]);
    setSwimmers([]);
    setBulkInput('');
  };

  // Add Single Swimmer
  const handleAddSwimmer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSwimmerName.trim()) return;

    const newSwimmer: SwimmerEntry = {
      id: `s_${Date.now()}`,
      name: newSwimmerName.trim(),
      age: parseInt(newSwimmerAge) || 12,
      gender: newSwimmerGender,
      team: newSwimmerTeam.trim().toUpperCase() || 'UNAT',
      eventName: newSwimmerEvent,
      seedTime: newSwimmerTime.trim() || 'NT',
      seedSeconds: parseTimeToSeconds(newSwimmerTime)
    };

    setSwimmers(prev => [...prev, newSwimmer]);
    setNewSwimmerName('');
    setNewSwimmerTime('NT');
  };

  // Add Single Event
  const handleAddEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEventName.trim()) return;

    const nextNumber = events.length > 0 ? Math.max(...events.map(ev => ev.number)) + 1 : 1;
    const newEvent: SwimEvent = {
      id: `e_${Date.now()}`,
      number: nextNumber,
      name: newEventName.trim()
    };

    setEvents(prev => [...prev, newEvent]);
    if (!newSwimmerEvent) {
      setNewSwimmerEvent(newEvent.name);
    }
    setNewEventName('');
  };

  // Parse Bulk Input
  const handleBulkImport = () => {
    if (!bulkInput.trim()) return;

    const lines = bulkInput.split('\n');
    const newSwimmers: SwimmerEntry[] = [];
    const addedEvents = [...events];

    lines.forEach((line, idx) => {
      const parts = line.split(',').map(p => p.trim());
      if (parts.length < 5) return;

      const [name, ageStr, genderStr, team, eventName, timeStr] = parts;
      if (!name || !eventName) return;

      // Handle Event Auto-Creation
      let ev = addedEvents.find(e => e.name.toLowerCase() === eventName.toLowerCase());
      if (!ev) {
        const nextNumber = addedEvents.length > 0 ? Math.max(...addedEvents.map(e => e.number)) + 1 : 1;
        ev = {
          id: `e_bulk_${Date.now()}_${idx}`,
          number: nextNumber,
          name: eventName
        };
        addedEvents.push(ev);
      }

      const seedTime = timeStr || 'NT';
      newSwimmers.push({
        id: `s_bulk_${Date.now()}_${idx}`,
        name,
        age: parseInt(ageStr) || 12,
        gender: (genderStr?.toUpperCase() === 'M' ? 'M' : genderStr?.toUpperCase() === 'F' ? 'F' : 'Mixed') as 'M' | 'F' | 'Mixed',
        team: team.toUpperCase() || 'UNAT',
        eventName: ev.name,
        seedTime,
        seedSeconds: parseTimeToSeconds(seedTime)
      });
    });

    if (newSwimmers.length > 0) {
      setEvents(addedEvents);
      setSwimmers(prev => [...prev, ...newSwimmers]);
      setBulkInput('');
    }
  };

  // Import from Connected Organization Roster
  const handleImportFromNode = (selectedMembers: DirectoryMember[]) => {
    if (selectedMembers.length === 0) return;
    
    const targetEvent = events[0] || {
      id: `ev-${Date.now()}`,
      number: 1,
      name: 'Open 50 Yard Freestyle'
    };

    if (events.length === 0) {
      setEvents([targetEvent]);
    }

    const teamAbbr = nodeName ? nodeName.slice(0, 4).toUpperCase() : 'CLUB';

    const newEntries: SwimmerEntry[] = selectedMembers.map((m, idx) => ({
      id: `swim-${Date.now()}-${idx}-${Math.random().toString(36).substring(2, 6)}`,
      name: m.name || 'Member',
      age: 12,
      gender: 'Mixed',
      team: teamAbbr,
      eventName: targetEvent.name,
      seedTime: 'NT',
      seedSeconds: parseTimeToSeconds('NT')
    }));

    setSwimmers(prev => [...prev, ...newEntries]);
    toast.success(`Imported ${newEntries.length} members from ${nodeName || 'organization'} into "${targetEvent.name}"`);
  };

  // Delete Swimmer
  const deleteSwimmer = (id: string) => {
    setSwimmers(prev => prev.filter(s => s.id !== id));
  };

  // Delete Event
  const deleteEvent = (id: string) => {
    const eventToDelete = events.find(e => e.id === id);
    if (!eventToDelete) return;
    setEvents(prev => prev.filter(e => e.id !== id));
    // Optionally clean up swimmers registered in that event
    setSwimmers(prev => prev.filter(s => s.eventName !== eventToDelete.name));
  };

  // Main Seeding Engine
  const seededProgram = useMemo((): SeededEvent[] => {
    const laneOrder = getLaneOrder(numLanes);
    
    return events
      .sort((a, b) => a.number - b.number)
      .map(event => {
        // Group and sort swimmers in this event by seed time
        // Since it's timed finals, we sort slowest-to-fastest (NT first, then descending speed, so slowest is at the start)
        const eventSwimmers = swimmers
          .filter(s => s.eventName === event.name)
          .sort((a, b) => {
            // Sort NT / Infinity first (slowest)
            if (a.seedSeconds === b.seedSeconds) return a.name.localeCompare(b.name);
            return b.seedSeconds - a.seedSeconds; // descending order, so Infinity is first, then larger seconds, down to smallest
          });

        const totalSwimmers = eventSwimmers.length;
        if (totalSwimmers === 0) {
          return { event, heats: [] };
        }

        // Calculate Heats
        const numHeats = Math.ceil(totalSwimmers / numLanes);
        
        // Distribute swimmers evenly across heats (Timed Finals balance)
        const heats: Heat[] = Array.from({ length: numHeats }, (_, idx) => ({
          heatNumber: idx + 1,
          lanes: {}
        }));

        // Calculate sizes of each heat
        const baseSize = Math.floor(totalSwimmers / numHeats);
        const extra = totalSwimmers % numHeats;
        
        const heatSizes: number[] = [];
        for (let i = 0; i < numHeats; i++) {
          // Earlier heats get base size, later heats get +1 if there are extras
          heatSizes.push(i < numHeats - extra ? baseSize : baseSize + 1);
        }

        // Slice swimmers into heats
        let swimmerIdx = 0;
        heats.forEach((heat, heatIdx) => {
          const size = heatSizes[heatIdx];
          const heatSwimmers = eventSwimmers.slice(swimmerIdx, swimmerIdx + size);
          swimmerIdx += size;

          // Seed this specific heat center-out
          // 1. Sort swimmers in the heat FASTEST first (ascending seconds)
          const sortedHeatSwimmers = [...heatSwimmers].sort((a, b) => a.seedSeconds - b.seedSeconds);
          
          // 2. Assign center-out lanes based on sorted positions
          sortedHeatSwimmers.forEach((swimmer, sortedPos) => {
            const laneNum = laneOrder[sortedPos];
            if (laneNum !== undefined) {
              heat.lanes[laneNum] = {
                ...swimmer,
                heat: heat.heatNumber,
                lane: laneNum
              };
            }
          });
        });

        return { event, heats };
      });
  }, [events, swimmers, numLanes]);

  // Standard Text Output Generator
  const generateTextOutput = (): string => {
    let output = `====================================================\n`;
    output += `${meetName.toUpperCase()}\n`;
    output += `Seeding Program - ${numLanes} Lanes (${distanceUnit})\n`;
    output += `====================================================\n\n`;

    seededProgram.forEach(seeded => {
      if (seeded.heats.length === 0) return;
      output += `Event ${seeded.event.number}  ${seeded.event.name}\n`;
      output += `====================================================\n`;
      output += `Lane  Name                 Age  Team      Seed Time\n`;
      output += `====================================================\n`;

      seeded.heats.forEach(heat => {
        output += `Heat ${heat.heatNumber} of ${seeded.heats.length}\n`;
        
        // Output lanes in physical numerical order (1 to N)
        for (let l = 1; l <= numLanes; l++) {
          const swimmer = heat.lanes[l];
          if (swimmer) {
            const nameStr = swimmer.name.padEnd(20).substring(0, 20);
            const ageStr = swimmer.age.toString().padStart(2);
            const teamStr = swimmer.team.padEnd(8).substring(0, 8);
            const timeStr = swimmer.seedTime.padStart(10);
            output += `  ${l}   ${nameStr}  ${ageStr}  ${teamStr}  ${timeStr}\n`;
          }
        }
        output += `\n`;
      });
      output += `\n`;
    });

    return output;
  };

  const handleCopyToClipboard = () => {
    const text = generateTextOutput();
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-8 text-left">
      
      {/* Hero Banner (Brutalist Style) */}
      <div className="bg-[#0044FF] border-8 border-black text-white p-8 md:p-12 relative overflow-hidden shadow-[8px_8px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[8px_8px_0px_#FFFFFF] print:hidden">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-[#FFD600] rounded-full border-8 border-black pointer-events-none" />
        <div className="relative z-10 space-y-4">
          <span className="bg-black text-white px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest border-2 border-black dark:border-white">
            Meet Seeding Utility
          </span>
          <h2 className="text-4xl md:text-6xl font-black font-display uppercase tracking-tighter leading-none break-words">
            Swim Meet Seeder
          </h2>
          <p className="max-w-xl text-lg font-bold text-blue-100 dark:text-gray-300">
            Generate clean, balanced heats and lane assignments in seconds. Paste entries, choose pool size, and print or export.
          </p>
        </div>
      </div>

      {/* Meet Settings Form (Brutalist Panels) */}
      <div className="grid md:grid-cols-3 gap-6 print:hidden">
        <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] md:col-span-2 space-y-4">
          <h3 className="font-display font-black text-xl uppercase tracking-tight">Meet Properties</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Meet Name</label>
              <Input 
                value={meetName} 
                onChange={e => setMeetName(e.target.value)} 
                placeholder="Meet Title"
                className="border-2 border-black rounded-none h-10 font-bold"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Lanes</label>
                <select 
                  value={numLanes} 
                  onChange={e => setNumLanes(parseInt(e.target.value) || 6)}
                  className="w-full border-2 border-black rounded-none h-10 px-2 font-mono font-bold bg-white dark:bg-black dark:text-white"
                >
                  <option value={4}>4 Lanes</option>
                  <option value={6}>6 Lanes</option>
                  <option value={8}>8 Lanes</option>
                  <option value={10}>10 Lanes</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Pool Lg.</label>
                <Input 
                  value={poolLength} 
                  onChange={e => setPoolLength(e.target.value)} 
                  placeholder="25"
                  className="border-2 border-black rounded-none h-10 font-mono font-bold"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Unit</label>
                <select 
                  value={distanceUnit} 
                  onChange={e => setDistanceUnit(e.target.value as 'Yards' | 'Meters')}
                  className="w-full border-2 border-black rounded-none h-10 px-2 font-bold bg-white dark:bg-black dark:text-white"
                >
                  <option value="Yards">Yds</option>
                  <option value="Meters">Mtr</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] flex flex-col justify-between">
          <div>
            <h3 className="font-display font-black text-xl uppercase tracking-tight mb-2">Controls</h3>
            <p className="text-xs text-gray-500 mb-4">Quick setup templates to populate values instantly.</p>
          </div>
          <div className="flex flex-col gap-2">
            <Button 
              onClick={loadSampleData} 
              variant="secondary" 
              className="border-2 border-black rounded-none uppercase font-mono tracking-widest text-xs font-bold hover:bg-[#FFD600]"
            >
              <RefreshCw size={14} className="mr-2" /> Load Demo Data
            </Button>
            <Button 
              onClick={clearAllData} 
              variant="ghost" 
              className="border-2 border-red-600 text-red-600 rounded-none uppercase font-mono tracking-widest text-xs font-bold hover:bg-red-50 dark:hover:bg-red-950/20"
            >
              Clear All Entries
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b-4 border-black dark:border-white print:hidden">
        <button
          onClick={() => setActiveTab('entries')}
          className={`px-6 py-3 font-display font-black uppercase text-lg tracking-tight border-t-4 border-l-4 border-r-4 border-black -mb-1 transition-all ${
            activeTab === 'entries'
              ? 'bg-[#FFD600] text-black border-b-white dark:border-b-black'
              : 'bg-white text-black hover:bg-gray-100 dark:bg-slate-900 dark:text-white'
          }`}
        >
          1. Swimmers & Events
        </button>
        <button
          onClick={() => setActiveTab('preview')}
          className={`px-6 py-3 font-display font-black uppercase text-lg tracking-tight border-t-4 border-l-4 border-r-4 border-black -mb-1 transition-all ${
            activeTab === 'preview'
              ? 'bg-[#FFD600] text-black border-b-white dark:border-b-black'
              : 'bg-white text-black hover:bg-gray-100 dark:bg-slate-900 dark:text-white'
          }`}
        >
          2. Seeded Heat Sheets
        </button>
        <button
          onClick={() => setActiveTab('timers')}
          className={`px-6 py-3 font-display font-black uppercase text-lg tracking-tight border-t-4 border-l-4 border-r-4 border-black -mb-1 transition-all ${
            activeTab === 'timers'
              ? 'bg-[#FFD600] text-black border-b-white dark:border-b-black'
              : 'bg-white text-black hover:bg-gray-100 dark:bg-slate-900 dark:text-white'
          }`}
        >
          3. Lane Timer Sheets
        </button>
      </div>

      {/* Tab CONTENT 1: Setup & Entries */}
      {activeTab === 'entries' && (
        <div className="grid lg:grid-cols-3 gap-8 print:hidden">
          
          {/* Form Entries Column */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Bulk Import */}
            <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-display font-black text-xl uppercase tracking-tight">Bulk Paste Swimmers</h3>
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#0044FF] bg-blue-50 px-2 py-0.5 border border-blue-200">CSV Mode</span>
              </div>

              {hasActiveNode ? (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-[#FFD600]/20 border-2 border-black dark:border-white">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-black text-white dark:bg-white dark:text-black flex items-center justify-center shrink-0 border border-black dark:border-white">
                      <Users size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-black uppercase tracking-tight text-black dark:text-white truncate">
                        {nodeName} Directory Connected
                      </p>
                      <p className="text-[10px] text-gray-600 dark:text-gray-400 font-bold">
                        {members.length} organization members available to import
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setIsRosterModalOpen(true)}
                    className="bg-[#0044FF] text-white hover:bg-blue-700 font-bold uppercase font-mono tracking-widest text-xs px-4 py-2.5 rounded-none border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] shrink-0"
                  >
                    Import Org Roster
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-[#FFD600]/15 border-2 border-black dark:border-white">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-[#0044FF] text-white flex items-center justify-center shrink-0 border border-black dark:border-white shadow-[2px_2px_0px_#000000]">
                      <Users size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-black uppercase tracking-tight text-black dark:text-white">
                        Running a Swim Team or Summer League?
                      </p>
                      <p className="text-[10px] text-gray-700 dark:text-gray-300 font-bold">
                        Connect an OrgSets team org to save swimmer seed times across meets, collect fees, and track parent waivers.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/sign-up')}
                    className="bg-black text-white hover:bg-[#0044FF] dark:bg-white dark:text-black dark:hover:bg-[#FFD600] font-black uppercase font-mono tracking-widest text-xs px-4 py-2.5 rounded-none border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] shrink-0"
                  >
                    Create Free Team Org
                  </Button>
                </div>
              )}

              <p className="text-xs text-gray-500 leading-normal">
                Paste comma-separated rows. Form: <code className="font-mono bg-gray-100 dark:bg-slate-800 px-1 py-0.5 rounded font-bold">Name, Age, Gender, Team, Event Name, Seed Time</code>
              </p>
              <textarea
                value={bulkInput}
                onChange={e => setBulkInput(e.target.value)}
                placeholder="Katie Ledecky, 26, F, NATION, Women 400 Yard Freestyle, 3:57.90&#10;Michael Phelps, 38, M, NORTH, Men 200 Yard Butterfly, 1:51.51"
                className="w-full h-36 border-2 border-black p-3 font-mono text-sm rounded-none resize-none focus:ring-0 focus:outline-none bg-white dark:bg-black dark:text-white"
              />
              <Button 
                onClick={handleBulkImport}
                disabled={!bulkInput.trim()}
                className="w-full bg-black text-white rounded-none border-2 border-black font-bold uppercase font-mono tracking-widest py-3 hover:bg-[#0044FF]"
              >
                Import Roster
              </Button>
            </div>

            {/* Individual Event Form & List */}
            <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-6">
              <h3 className="font-display font-black text-xl uppercase tracking-tight">Manage Events</h3>
              
              <form onSubmit={handleAddEvent} className="flex gap-2">
                <Input 
                  value={newEventName}
                  onChange={e => setNewEventName(e.target.value)}
                  placeholder="e.g. Girls 13-14 50 Yard Freestyle"
                  className="border-2 border-black rounded-none h-10 font-bold"
                />
                <Button type="submit" className="bg-[#0044FF] text-white border-2 border-black rounded-none px-6 font-mono font-bold uppercase hover:bg-blue-700 shrink-0">
                  Add Event
                </Button>
              </form>

              {events.length === 0 ? (
                <p className="text-sm font-mono text-gray-500 uppercase py-4 text-center border-2 border-dashed border-gray-300">No events defined. Add one above.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2">
                  {events.map(ev => {
                    const count = swimmers.filter(s => s.eventName === ev.name).length;
                    return (
                      <div key={ev.id} className="flex items-center justify-between border-2 border-black p-3 bg-[#F5F5F0] dark:bg-slate-900">
                        <div>
                          <span className="font-mono text-xs font-black bg-black text-white dark:bg-white dark:text-black px-1.5 py-0.5 mr-2">#{ev.number}</span>
                          <span className="font-bold text-sm uppercase tracking-tight">{ev.name}</span>
                          <span className="text-[10px] font-mono text-gray-500 block mt-1">{count} Swimmers</span>
                        </div>
                        <button onClick={() => deleteEvent(ev.id)} className="text-red-600 hover:text-red-800 p-1 border border-transparent hover:border-black transition-colors">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Swimmers Roster */}
            <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
              <h3 className="font-display font-black text-xl uppercase tracking-tight">Active Swimmers List ({swimmers.length})</h3>
              
              {swimmers.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-300 text-gray-400 font-mono uppercase text-xs">
                  Roster is empty. Import or add swimmers individually.
                </div>
              ) : (
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full border-collapse text-xs text-left">
                    <thead>
                      <tr className="border-b-4 border-black dark:border-white bg-[#F5F5F0] dark:bg-slate-900 font-mono font-bold uppercase tracking-wider">
                        <th className="py-2 px-3">Name</th>
                        <th className="py-2 px-3">Age/Sex</th>
                        <th className="py-2 px-3">Team</th>
                        <th className="py-2 px-3">Event</th>
                        <th className="py-2 px-3 text-right">Seed Time</th>
                        <th className="py-2 px-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y-2 divide-gray-200 dark:divide-slate-800 font-bold">
                      {swimmers.map(s => (
                        <tr key={s.id} className="hover:bg-[#FFD600]/10 transition-colors">
                          <td className="py-2 px-3 font-display uppercase tracking-tight text-sm">{s.name}</td>
                          <td className="py-2 px-3 font-mono">{s.age} / {s.gender}</td>
                          <td className="py-2 px-3 font-mono">{s.team}</td>
                          <td className="py-2 px-3 truncate max-w-[150px]">{s.eventName}</td>
                          <td className="py-2 px-3 font-mono text-right text-blue-600 dark:text-blue-400">{s.seedTime}</td>
                          <td className="py-2 px-3 text-center">
                            <button onClick={() => deleteSwimmer(s.id)} className="text-red-500 hover:text-red-700">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>

          {/* Quick Add Swimmer Form */}
          <div className="lg:col-span-1">
            <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4 sticky top-28">
              <h3 className="font-display font-black text-xl uppercase tracking-tight">Add Swimmer</h3>
              
              <form onSubmit={handleAddSwimmer} className="space-y-3 text-sm">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-[10px] uppercase font-bold text-gray-500 block">Swimmer Name</label>
                    {hasActiveNode && members.length > 0 && (
                      <span className="text-[9px] font-mono font-bold text-[#0044FF] uppercase tracking-widest">
                        Roster Linked
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input 
                      value={newSwimmerName} 
                      onChange={e => setNewSwimmerName(e.target.value)} 
                      placeholder="Enter name" 
                      className="border-2 border-black rounded-none h-10 font-bold flex-1"
                    />
                    {hasActiveNode && members.length > 0 && (
                      <select
                        onChange={e => {
                          if (e.target.value) {
                            setNewSwimmerName(e.target.value);
                            if (nodeName) {
                              setNewSwimmerTeam(nodeName.slice(0, 4).toUpperCase());
                            }
                          }
                        }}
                        value=""
                        className="border-2 border-black rounded-none h-10 px-2 text-xs font-mono font-bold bg-[#FFD600] text-black shrink-0 cursor-pointer max-w-[120px]"
                        title="Pick member from organization roster"
                      >
                        <option value="" disabled>Member...</option>
                        {members.map(m => (
                          <option key={m.id} value={m.name}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Age</label>
                    <Input 
                      type="number" 
                      value={newSwimmerAge} 
                      onChange={e => setNewSwimmerAge(e.target.value)} 
                      placeholder="12"
                      className="border-2 border-black rounded-none h-10 font-mono font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Gender</label>
                    <select 
                      value={newSwimmerGender} 
                      onChange={e => setNewSwimmerGender(e.target.value as 'M' | 'F' | 'Mixed')}
                      className="w-full border-2 border-black rounded-none h-10 px-2 font-bold bg-white dark:bg-black dark:text-white"
                    >
                      <option value="M">Male (M)</option>
                      <option value="F">Female (F)</option>
                      <option value="Mixed">Mixed</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Team Abbr.</label>
                    <Input 
                      value={newSwimmerTeam} 
                      onChange={e => setNewSwimmerTeam(e.target.value)} 
                      placeholder="WEST" 
                      className="border-2 border-black rounded-none h-10 font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Seed Time</label>
                    <Input 
                      value={newSwimmerTime} 
                      onChange={e => setNewSwimmerTime(e.target.value)} 
                      placeholder="NT or SS.hh" 
                      className="border-2 border-black rounded-none h-10 font-mono font-bold"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Register In Event</label>
                  <select 
                    value={newSwimmerEvent} 
                    onChange={e => setNewSwimmerEvent(e.target.value)}
                    className="w-full border-2 border-black rounded-none h-10 px-2 font-bold bg-white dark:bg-black dark:text-white"
                  >
                    {events.map(ev => (
                      <option key={ev.id} value={ev.name}>Event {ev.number}: {ev.name}</option>
                    ))}
                  </select>
                </div>

                <Button 
                  type="submit" 
                  disabled={events.length === 0}
                  className="w-full bg-[#FFD600] text-black border-2 border-black rounded-none font-bold uppercase tracking-wider py-3 hover:bg-black hover:text-white"
                >
                  Save Swimmer
                </Button>
              </form>
            </div>
          </div>

        </div>
      )}

      {/* Tab CONTENT 2: Preview Heat Sheets */}
      {activeTab === 'preview' && (
        <div className="space-y-8">
          
          {/* Actions Bar */}
          <div className="bg-[#F5F5F0] border-4 border-black p-4 flex flex-col sm:flex-row justify-between items-center gap-4 dark:bg-slate-900 print:hidden">
            <div className="flex items-center gap-2">
              <Info size={16} className="text-blue-600" />
              <span className="text-xs font-mono font-bold uppercase text-gray-600 dark:text-gray-300">
                Seeding automatically generated center-out for standard timed finals.
              </span>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
              <Button 
                onClick={() => setShowShareModal(true)} 
                className="bg-[#FFD600] text-black border-2 border-black rounded-none uppercase font-mono tracking-widest text-xs font-black hover:bg-[#E6C200] shadow-[2px_2px_0px_#000] w-full sm:w-auto flex items-center justify-center gap-1.5"
              >
                <Share2 size={14} /> Share with Parents
              </Button>
              <Button 
                onClick={handleCopyToClipboard} 
                variant="secondary" 
                className="border-2 border-black rounded-none uppercase font-mono tracking-widest text-xs font-bold hover:bg-[#FFD600] w-full sm:w-auto"
              >
                {copySuccess ? (
                  <>
                    <Check size={14} className="mr-2 text-green-600" /> Copied Text
                  </>
                ) : (
                  <>
                    <Clipboard size={14} className="mr-2" /> Copy TXT Output
                  </>
                )}
              </Button>
              <Button 
                onClick={() => window.print()} 
                className="bg-black text-white border-2 border-black rounded-none uppercase font-mono tracking-widest text-xs font-bold hover:bg-[#0044FF] w-full sm:w-auto"
              >
                <Printer size={14} className="mr-2" /> Print PDF Page
              </Button>
            </div>
          </div>

          {/* Seeded Heat Sheets Document Display */}
          <div className="bg-white border-4 border-black p-6 sm:p-8 dark:bg-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] print:border-none print:shadow-none print:p-0">
            
            {/* Header Document Section */}
            <div className="text-center border-b-8 border-black dark:border-white pb-6 mb-8 text-black dark:text-white">
              <h1 className="text-3xl sm:text-4xl font-display font-black uppercase tracking-tight">
                {meetName}
              </h1>
              <p className="text-xs font-mono uppercase font-bold text-gray-500 tracking-widest mt-2">
                Official Heat Sheet & Seeding Matrix • Pool: {poolLength} {distanceUnit} • {numLanes} Lanes
              </p>
            </div>

            {/* Events Loop */}
            {seededProgram.filter(s => s.heats.length > 0).length === 0 ? (
              <div className="text-center py-16 text-gray-500 font-mono uppercase text-xs border-2 border-dashed border-gray-300">
                No swimmers entered in any event. Seed lists are empty.
              </div>
            ) : (
              <div className="space-y-12">
                {seededProgram.map(seeded => {
                  if (seeded.heats.length === 0) return null;

                  return (
                    <div key={seeded.event.id} className="space-y-6 page-break-after-always">
                      
                      {/* Event Banner */}
                      <div className="border-b-4 border-black dark:border-white pb-2 flex justify-between items-end text-black dark:text-white">
                        <h2 className="text-xl sm:text-2xl font-display font-black uppercase tracking-tight">
                          Event {seeded.event.number} &nbsp; {seeded.event.name}
                        </h2>
                        <span className="font-mono text-xs font-bold text-gray-500 uppercase tracking-widest">
                          {seeded.heats.length} {seeded.heats.length === 1 ? 'Heat' : 'Heats'}
                        </span>
                      </div>

                      {/* Heats Grid */}
                      <div className="space-y-8">
                        {seeded.heats.map(heat => (
                          <div key={heat.heatNumber} className="space-y-4 text-black dark:text-white">
                            
                            {/* Heat Title */}
                            <div className="bg-[#F5F5F0] border-2 border-black px-3 py-1.5 font-mono text-xs font-bold uppercase tracking-wider flex justify-between items-center dark:bg-slate-900">
                              <span>Heat {heat.heatNumber} of {seeded.heats.length}</span>
                              <span className="text-[10px] text-gray-500">Center-out Lanes</span>
                            </div>

                            {/* Heat Table */}
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse text-sm text-left">
                                <thead>
                                  <tr className="border-b-2 border-black dark:border-white font-mono text-xs font-bold uppercase text-gray-500 tracking-wider">
                                    <th className="py-1 px-3 w-16 text-center">Lane</th>
                                    <th className="py-1 px-3">Name</th>
                                    <th className="py-1 px-3 w-16 text-center">Age</th>
                                    <th className="py-1 px-3 w-20">Team</th>
                                    <th className="py-1 px-3 w-28 text-right">Seed Time</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y border-b font-bold">
                                  {Array.from({ length: numLanes }, (_, laneIdx) => {
                                    const laneNum = laneIdx + 1;
                                    const swimmer = heat.lanes[laneNum];
                                    
                                    return (
                                      <tr key={laneNum} className={swimmer ? 'hover:bg-slate-50 dark:hover:bg-slate-900/30' : 'text-gray-300 dark:text-slate-800'}>
                                        <td className="py-2 px-3 text-center font-mono border-r-2 border-gray-100 dark:border-slate-800">
                                          <span className={`inline-flex items-center justify-center w-6 h-6 border-2 font-mono font-black text-xs ${
                                            swimmer 
                                              ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white' 
                                              : 'bg-transparent text-gray-300 border-gray-200 dark:border-slate-800 dark:text-slate-800'
                                          }`}>
                                            {laneNum}
                                          </span>
                                        </td>
                                        <td className="py-2 px-3 truncate max-w-[200px]">
                                          {swimmer ? (
                                            <span className="font-display uppercase tracking-tight text-sm font-bold text-black dark:text-white">
                                              {swimmer.name}
                                            </span>
                                          ) : (
                                            <span className="font-mono text-xs italic tracking-widest text-gray-300 dark:text-slate-800">EMPTY LANE</span>
                                          )}
                                        </td>
                                        <td className="py-2 px-3 text-center font-mono">
                                          {swimmer ? swimmer.age : ''}
                                        </td>
                                        <td className="py-2 px-3 font-mono">
                                          {swimmer ? swimmer.team : ''}
                                        </td>
                                        <td className="py-2 px-3 text-right font-mono text-blue-600 dark:text-blue-400">
                                          {swimmer ? swimmer.seedTime : ''}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            {/* Graphical Pool Lanes Visualizer (WOW Factor) */}
                            <div className="border-4 border-black p-4 bg-[#F5F5F0] dark:bg-slate-950 dark:border-white print:hidden">
                              <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-gray-500 block mb-3">Pool Deck Visualizer (Heat {heat.heatNumber})</span>
                              <div className="flex flex-col border-t-4 border-b-4 border-blue-500 divide-y-2 divide-blue-300 overflow-x-auto">
                                {Array.from({ length: numLanes }, (_, laneIdx) => {
                                  const laneNum = laneIdx + 1;
                                  const swimmer = heat.lanes[laneNum];
                                  return (
                                    <div key={laneNum} className="flex h-10 items-center bg-sky-50/50 hover:bg-sky-100/50 transition-colors dark:bg-slate-900/20">
                                      {/* Lane Number */}
                                      <div className="w-12 bg-blue-500 text-white font-mono font-black flex items-center justify-center h-full border-r-2 border-blue-600">
                                        L{laneNum}
                                      </div>
                                      {/* Swimmer Placement */}
                                      <div className="flex-1 px-4 flex justify-between items-center text-xs truncate">
                                        {swimmer ? (
                                          <>
                                            <span className="font-display uppercase font-black tracking-tight text-blue-900 dark:text-blue-300">{swimmer.name}</span>
                                            <span className="font-mono text-blue-700 dark:text-blue-400 text-[10px] font-bold bg-white dark:bg-black border border-blue-400 px-1.5 py-0.5">{swimmer.team} • {swimmer.seedTime}</span>
                                          </>
                                        ) : (
                                          <span className="text-gray-400/60 font-mono italic text-[10px] tracking-wider">LANE EMPTY (water only)</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Document Footer */}
            <div className="mt-8 pt-4 border-t-2 border-black dark:border-white flex flex-col sm:flex-row justify-between items-center text-[10px] font-mono font-bold text-gray-500 uppercase tracking-widest gap-2">
              <span>Program generated by OrgSets Swim Seeder</span>
              <span>Manage your swim team at orgsets.com</span>
            </div>
          </div>

        </div>
      )}

      {/* Tab CONTENT 3: Lane Timer Sheets */}
      {activeTab === 'timers' && (
        <div className="space-y-8">
          {/* Timer Controls Bar */}
          <div className="bg-[#FFD600] border-4 border-black text-black p-6 shadow-[4px_4px_0px_#000000] flex flex-col md:flex-row justify-between items-start md:items-center gap-4 print:hidden">
            <div>
              <span className="text-[10px] font-mono font-bold uppercase tracking-widest bg-black text-white px-2 py-0.5 mb-1 inline-block">
                Volunteer Deck Sheets
              </span>
              <h3 className="font-display font-black text-2xl uppercase tracking-tight">
                Lane Timer Recording Sheets
              </h3>
              <p className="text-xs font-bold text-black/80 max-w-xl">
                Print dedicated timer cards for each lane. Timers record 3 raw stopwatch times per heat.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => window.print()}
                variant="secondary"
                className="bg-black text-white hover:bg-[#0044FF] border-2 border-black rounded-none uppercase font-mono tracking-widest text-xs font-bold"
              >
                <Printer size={14} className="mr-2" /> Print Timer Sheets
              </Button>
            </div>
          </div>

          {/* Lane Filter Buttons */}
          <div className="flex flex-wrap gap-2 items-center print:hidden">
            <span className="text-xs font-mono font-bold uppercase tracking-widest mr-2">Filter Lane:</span>
            <button
              onClick={() => setTimerLaneFilter('all')}
              className={`px-3 py-1 text-xs font-mono font-bold border-2 border-black cursor-pointer ${
                timerLaneFilter === 'all' ? 'bg-black text-white' : 'bg-white text-black'
              }`}
            >
              All Lanes (1 Sheet Per Lane)
            </button>
            {Array.from({ length: numLanes }).map((_, idx) => {
              const lane = idx + 1;
              return (
                <button
                  key={lane}
                  onClick={() => setTimerLaneFilter(lane)}
                  className={`px-3 py-1 text-xs font-mono font-bold border-2 border-black cursor-pointer ${
                    timerLaneFilter === lane ? 'bg-[#FFD600] text-black' : 'bg-white text-black'
                  }`}
                >
                  Lane {lane}
                </button>
              );
            })}
          </div>

          {/* Timer Sheets Content */}
          <div className="space-y-12">
            {Array.from({ length: numLanes }).map((_, idx) => {
              const lane = idx + 1;
              if (timerLaneFilter !== 'all' && timerLaneFilter !== lane) return null;

              return (
                <div key={lane} className="bg-white border-4 border-black p-6 sm:p-8 text-black print:p-2 print:border-2 print:border-black break-after-page shadow-[4px_4px_0px_#000]">
                  {/* Sheet Header */}
                  <div className="border-b-4 border-black pb-4 mb-4 flex justify-between items-end">
                    <div>
                      <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-slate-500">
                        {meetName || 'Swim Meet'} • {poolLength} {distanceUnit}
                      </span>
                      <h2 className="text-3xl font-black font-display uppercase tracking-tight text-black mt-1">
                        LANE {lane} — TIMER RECORDING SHEET
                      </h2>
                    </div>
                    <div className="text-right font-mono text-xs">
                      <span className="bg-black text-white px-2 py-0.5 font-bold">LANE {lane}</span>
                      <p className="text-[10px] text-slate-500 mt-1">3 Timers Required</p>
                    </div>
                  </div>

                  <p className="text-xs font-mono text-slate-600 mb-4 bg-slate-50 p-2 border border-slate-300">
                    <strong>Timer Instructions:</strong> Start watches on strobe/horn. Stop watches precisely when swimmer touches wall. Record all 3 stopwatch times. If disqualification observed, notify Chief Judge immediately.
                  </p>

                  {/* Table of Heats for this lane */}
                  <table className="w-full text-left border-collapse border-2 border-black text-xs font-mono">
                    <thead>
                      <tr className="bg-slate-100 border-b-2 border-black">
                        <th className="p-2 border-r border-black w-14 text-center">Ev #</th>
                        <th className="p-2 border-r border-black">Event Name</th>
                        <th className="p-2 border-r border-black w-14 text-center">Heat</th>
                        <th className="p-2 border-r border-black">Swimmer & Team</th>
                        <th className="p-2 border-r border-black w-20 text-right">Seed</th>
                        <th className="p-2 border-r border-black w-24 text-center bg-blue-50">Watch 1</th>
                        <th className="p-2 border-r border-black w-24 text-center bg-blue-50">Watch 2</th>
                        <th className="p-2 border-r border-black w-24 text-center bg-blue-50">Watch 3</th>
                        <th className="p-2 w-28 text-center bg-yellow-50 font-black">Official Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seededProgram.flatMap((se) =>
                        se.heats.map((heat) => {
                          const swimmer = heat.lanes[lane];
                          return (
                            <tr key={`${se.event.id}-h${heat.heatNumber}-l${lane}`} className="border-b border-black/30 h-10">
                              <td className="p-2 border-r border-black text-center font-bold">{se.event.number}</td>
                              <td className="p-2 border-r border-black truncate max-w-[140px] font-sans font-bold">{se.event.name}</td>
                              <td className="p-2 border-r border-black text-center font-bold">{heat.heatNumber}</td>
                              <td className="p-2 border-r border-black font-sans">
                                {swimmer ? (
                                  <span className="font-bold">{swimmer.name} <span className="text-[10px] text-slate-500 font-mono">({swimmer.team})</span></span>
                                ) : (
                                  <span className="text-slate-400 italic text-[10px]">-- Empty Lane --</span>
                                )}
                              </td>
                              <td className="p-2 border-r border-black text-right font-bold text-slate-600">
                                {swimmer ? swimmer.seedTime : '—'}
                              </td>
                              <td className="p-2 border-r border-black bg-blue-50/20"></td>
                              <td className="p-2 border-r border-black bg-blue-50/20"></td>
                              <td className="p-2 border-r border-black bg-blue-50/20"></td>
                              <td className="p-2 bg-yellow-50/20"></td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>

                  {/* Signatures */}
                  <div className="mt-8 pt-4 border-t-2 border-black grid grid-cols-3 gap-6 text-xs font-mono">
                    <div>
                      <p className="text-slate-500 mb-6">Timer 1 Name & Signature:</p>
                      <div className="border-b border-black"></div>
                    </div>
                    <div>
                      <p className="text-slate-500 mb-6">Timer 2 Name & Signature:</p>
                      <div className="border-b border-black"></div>
                    </div>
                    <div>
                      <p className="text-slate-500 mb-6">Chief Timer / Referee Initials:</p>
                      <div className="border-b border-black"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {hasActiveNode && (
        <NodeRosterPickerModal
          isOpen={isRosterModalOpen}
          onClose={() => setIsRosterModalOpen(false)}
          onImport={handleImportFromNode}
          title={`Import ${nodeName || 'Organization'} Swimmers`}
          description={`Select members from ${nodeName} to register in the current meet schedule.`}
          confirmLabel="Add Swimmers to Meet"
        />
      )}

      {/* Share with Parents Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm print:hidden">
          <div className="bg-white dark:bg-black border-[8px] border-black dark:border-white w-full max-w-lg shadow-[16px_16px_0px_#0044FF] dark:shadow-[16px_16px_0px_#FFD600] relative animate-in fade-in zoom-in-95 duration-200">
            <button 
              onClick={() => setShowShareModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-black dark:hover:text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="square" strokeLinejoin="miter"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
            <div className="p-8 md:p-10 space-y-6">
              <div className="w-16 h-16 bg-[#0044FF] border-[4px] border-black dark:border-white flex items-center justify-center shadow-[4px_4px_0px_#000] mb-6">
                <Share2 className="text-white" strokeWidth={3} size={32} />
              </div>
              <h2 className="text-3xl font-black font-display uppercase tracking-tighter text-black dark:text-white leading-[0.9]">
                Publish Live Mobile Meet Program
              </h2>
              <p className="text-base font-bold text-gray-600 dark:text-gray-400 leading-relaxed">
                Never crowd the paper bulletin board on deck again. Deploy an OrgSets Swim Org to give parents and coaches live, mobile-friendly heat sheets with real-time updates.
              </p>
              
              <div className="bg-[#F5F5F0] dark:bg-slate-900 border-2 border-black dark:border-white p-4 space-y-2 text-xs font-bold text-black dark:text-white">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                  <span>Mobile-friendly heat & lane lookups for families</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span>Instant SMS weather alerts & lightning delays</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#FFD600] rounded-full" />
                  <span>Automated parent timer & volunteer shift signups</span>
                </div>
              </div>

              <div className="pt-2 space-y-3">
                <button 
                  onClick={() => {
                    const orgName = localStorage.getItem('orgsets_lead_org') || meetName || 'Swim Club';
                    const slug = orgName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `swim-${Date.now().toString(36)}`;
                    const pendingData = {
                      name: orgName,
                      slug,
                      template: 'SPORTS_LEAGUE',
                      tier: 'LOCAL',
                      visibility: 'PUBLIC',
                      modules: DEFAULT_ENABLED_MODULES,
                      sourceTool: 'swim-heat-sheet-generator'
                    };
                    localStorage.setItem('pending_hub_creation', JSON.stringify(pendingData));
                    localStorage.setItem('pending_tool_org_draft', JSON.stringify({
                      toolSlug: 'swim-heat-sheet-generator',
                      toolTitle: 'Swim Meet Heat Sheet Generator',
                      orgName,
                      meetName,
                      swimmersCount: swimmers.length,
                      timestamp: new Date().toISOString()
                    }));
                    const leadEmail = localStorage.getItem('orgsets_lead_email');
                    navigate(leadEmail 
                      ? `/sign-up?redirect_url=/dashboard&source=tool&slug=swim-heat-sheet-generator&email=${encodeURIComponent(leadEmail)}`
                      : `/sign-up?redirect_url=/dashboard&source=tool&slug=swim-heat-sheet-generator`);
                  }}
                  className="w-full bg-[#0044FF] hover:bg-blue-700 text-white font-black uppercase tracking-widest px-6 py-4 text-base border-[4px] border-black dark:border-white transition-colors shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff]"
                >
                  Create Free Swim Team Org &amp; Claim Admin Account →
                </button>
                <button 
                  onClick={() => setShowShareModal(false)}
                  className="w-full text-center text-xs font-mono font-bold uppercase tracking-widest text-gray-500 hover:text-black dark:hover:text-white py-2 transition-colors cursor-pointer"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
